import React, { useState, useEffect } from 'react';
import {
  Package,
  PackageCheck,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader,
  ClipboardList,
  ShoppingCart
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { packingService } from '../../../../services/packingService';
import { PackingGroupDto, PackZoneDto } from '../../../../types/packing';
import { PackingDetailView } from './PackingDetailView';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { ScanTextInput } from '../../../common/inputs';

// Splitter kommaseparert PackZoneIDs fra backend til liste
const splitPackZoneIds = (value?: string | null): string[] =>
  (value ?? '').split(',').map((s) => s.trim()).filter(Boolean);

interface PackingAppProps {
  onBack?: () => void;
}

export const PackingApp: React.FC<PackingAppProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<PackingGroupDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPackZone, setSelectedPackZone] = useState<string>('');
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [orderNoFilter, setOrderNoFilter] = useState<string>('');
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [packZoneFilter, setPackZoneFilter] = useState<string>('');
  const [packZones, setPackZones] = useState<PackZoneDto[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'inProgress'>('all');

  // Build localized completion status
  const buildCompletionStatus = (group: PackingGroupDto): string => {
    const parts: string[] = [];

    // Packed/ready status
    if (group.numOfReadyPickLists > 0) {
      parts.push(t('warehouse.modules.packing.completionStatus.packed', {
        '0': group.numOfCompletedPickLists.toString(),
        '1': group.numOfReadyPickLists.toString()
      }));
    }

    // Ongoing picks
    if (group.numOfOngoingPickLists > 0) {
      parts.push(t('warehouse.modules.packing.completionStatus.ongoing', {
        '0': group.numOfOngoingPickLists.toString()
      }));
    }

    // Orders without pick lists
    if (group.numOfOrdersWithoutPickLists > 0) {
      const waitingKey = group.numOfOrdersWithoutPickLists === 1 ? 'waitingOne' : 'waiting';
      parts.push(t(`warehouse.modules.packing.completionStatus.${waitingKey}`, {
        '0': group.numOfOrdersWithoutPickLists.toString()
      }));
    }

    // If no parts, show simple status
    if (parts.length === 0) {
      return t('warehouse.modules.packing.completionStatus.noOrders');
    }

    return parts.join(', ');
  };

  // Map backend groupStatus → ikon + lokalisert tekst for status-badgen.
  // Bruker den rikere 5-tilstands-statusen fra backend (Waiting/InProgress/Ready/Complete/Mixed)
  // i stedet for den binære isComplete, slik at operatøren ser «Klar» (alle plukk ferdige,
  // klar til å pakke) som egen tilstand — ikke samme «Under arbeid» som pågående plukk.
  const getStatusDisplay = (groupStatus: string): { Icon: typeof Package; label: string } => {
    switch (groupStatus) {
      case 'Complete':
        return { Icon: CheckCircle, label: t('warehouse.modules.packing.status.complete', {}, 'Ferdig') };
      case 'Ready':
        return { Icon: PackageCheck, label: t('warehouse.modules.packing.status.ready', {}, 'Klar til pakking') };
      case 'InProgress':
        return { Icon: Clock, label: t('warehouse.modules.packing.status.inProgress', {}, 'Plukking pågår') };
      case 'Waiting':
        return { Icon: Clock, label: t('warehouse.modules.packing.status.waiting', {}, 'Venter på plukk') };
      case 'Mixed':
        return { Icon: AlertCircle, label: t('warehouse.modules.packing.status.mixed', {}, 'Blandet') };
      default:
        return { Icon: AlertCircle, label: t('warehouse.modules.packing.status.unknown', {}, 'Ukjent') };
    }
  };

  useEffect(() => {
    loadPackingGroups();
  }, [selectedPackZone]);

  // Pakkesoner (kode + beskrivelse) til søk/skann og visning av beskrivelse på kortene
  useEffect(() => {
    packingService
      .getPackZones()
      .then(setPackZones)
      .catch((err) => console.error('Error loading pack zones:', err));
  }, []);

  const getPackZoneLabel = (packZoneId: string): string =>
    packZones.find((z) => z.packZoneID === packZoneId)?.description || packZoneId;

  const loadPackingGroups = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await packingService.getPackingGroups(
        selectedPackZone || undefined
      );
      setGroups(data);
    } catch (err) {
      console.error('Error loading packing groups:', err);
      setError(
        err instanceof Error
          ? err.message
          : t('warehouse.modules.packing.errors.loadFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGroupClick = (group: PackingGroupDto) => {
    setSelectedGroupId(group.groupingId);
  };

  const handleBackFromDetail = () => {
    setSelectedGroupId(null);
    loadPackingGroups(); // Refresh list after returning
  };

  // Show detail view if a group is selected
  if (selectedGroupId) {
    return (
      <PackingDetailView
        groupingId={selectedGroupId}
        onBack={handleBackFromDetail}
        onComplete={() => loadPackingGroups()}
      />
    );
  }

  const handleRefresh = () => {
    loadPackingGroups();
  };

  // Pakkesone-søk: treff på kode/kollinr eller beskrivelse (delvis, uten store/små bokstaver).
  const packZoneTerm = packZoneFilter.trim().toLowerCase();
  const matchingPackZoneIds = new Set(
    packZones
      .filter(
        (z) =>
          z.packZoneID.toLowerCase().includes(packZoneTerm) ||
          (z.description ?? '').toLowerCase().includes(packZoneTerm)
      )
      .map((z) => z.packZoneID.toLowerCase())
  );

  // Klientside-filter (ordrenr + mottaker + pakkesone + status).
  const filteredGroups = groups.filter((group) => {
    if (packZoneTerm) {
      const groupZones = splitPackZoneIds(group.packZoneIDs).map((z) => z.toLowerCase());
      const zoneMatch = groupZones.some(
        (z) => matchingPackZoneIds.has(z) || z.includes(packZoneTerm)
      );
      if (!zoneMatch) return false;
    }
    const orderNo = orderNoFilter.trim().toLowerCase();
    if (orderNo && !(group.orderNumbers ?? '').toLowerCase().includes(orderNo)) {
      return false;
    }
    const customer = customerFilter.trim().toLowerCase();
    if (customer && !group.groupingVisibleId.toLowerCase().includes(customer)) {
      return false;
    }
    if (statusFilter === 'completed' && !group.isComplete) return false;
    if (statusFilter === 'inProgress' && group.isComplete) return false;
    return true;
  });
  const hasActiveFilters =
    orderNoFilter.trim() !== '' ||
    customerFilter.trim() !== '' ||
    packZoneTerm !== '' ||
    statusFilter !== 'all';

  // Loading state
  if (loading) {
    return (
      <div className="min-h-full bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              {onBack && <BackButton onClick={onBack} />}
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">{t('warehouse.modules.packing.title')}</h1>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-2" />
            <p className="text-sm sm:text-base text-gray-600">{t('warehouse.modules.packing.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-full bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              {onBack && <BackButton onClick={onBack} />}
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">{t('warehouse.modules.packing.title')}</h1>
              </div>
            </div>
          </div>
        </div>

        <div className="p-3 sm:p-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-start gap-2 sm:gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-red-900 mb-1">{t('common.errors.loadingError')}</h3>
                <p className="text-sm text-red-700 break-words">{error}</p>
                <button
                  onClick={handleRefresh}
                  className="mt-3 text-sm font-medium text-red-600 hover:text-red-700 min-h-[44px] inline-flex items-center"
                >
                  {t('common.buttons.tryAgain')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main view
  return (
    <div className="min-h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {onBack && <BackButton onClick={onBack} />}
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Pakking</h1>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            className="text-sm font-medium text-blue-600 hover:text-blue-700 flex-shrink-0 min-h-[44px] px-2"
          >
            {t('common.buttons.refresh')}
          </button>
        </div>

        {/* Summary */}
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
          <span className="font-medium">{filteredGroups.length}</span>
          <span>{filteredGroups.length === 1 ? t('warehouse.modules.packing.group') : t('warehouse.modules.packing.groups')}</span>
          {hasActiveFilters && <span className="text-gray-400">/ {groups.length}</span>}
        </div>

        {/* Filters */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={orderNoFilter}
            onChange={(e) => setOrderNoFilter(e.target.value)}
            placeholder={t('warehouse.modules.packing.filters.orderNumber')}
            className="flex-1 min-w-[120px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <input
            type="text"
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            placeholder={t('warehouse.modules.packing.filters.customer')}
            className="flex-1 min-w-[120px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <ScanTextInput
            value={packZoneFilter}
            onChange={(e) => setPackZoneFilter(e.target.value)}
            onKeyDown={(e) => {
              // Skanner sender Enter — filteret er allerede aktivt, så bare hindre submit/fokusflytt
              if (e.key === 'Enter') e.preventDefault();
            }}
            placeholder={t('warehouse.modules.packing.filters.packZone', {}, 'Kollinr/pakkesone')}
            className="flex-1 min-w-[120px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'completed' | 'inProgress')}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="all">{t('warehouse.modules.packing.filters.statusAll')}</option>
            <option value="completed">{t('common.status.completed')}</option>
            <option value="inProgress">{t('common.status.inProgress')}</option>
          </select>
          {hasActiveFilters && (
            <button
              onClick={() => {
                setOrderNoFilter('');
                setCustomerFilter('');
                setPackZoneFilter('');
                setStatusFilter('all');
              }}
              className="text-sm font-medium text-blue-600 hover:text-blue-700 px-2 min-h-[44px]"
            >
              {t('warehouse.modules.packing.filters.clear')}
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4">
        {filteredGroups.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6 sm:p-8 text-center">
            <Package className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1">
              {groups.length === 0
                ? t('warehouse.modules.packing.noGroups')
                : t('warehouse.modules.packing.filters.noMatch')}
            </h3>
            <p className="text-sm text-gray-600">
              {groups.length === 0
                ? t('warehouse.modules.packing.noGroupsDescription')
                : t('warehouse.modules.packing.filters.noMatchDescription')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {filteredGroups.map((group) => {
              const status = getStatusDisplay(group.groupStatus);
              return (
              <div
                key={group.groupingId}
                onClick={() => handleGroupClick(group)}
                className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer min-h-[44px] min-w-0"
              >
                {/* Header with status badge */}
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                      {group.groupingVisibleId}
                    </h3>
                  </div>
                  <div
                    className={`flex-shrink-0 px-2 sm:px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 sm:gap-1.5 ${packingService.getGroupStatusColor(
                      group.groupStatus
                    )}`}
                  >
                    <status.Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                    <span className="whitespace-nowrap">{status.label}</span>
                  </div>
                </div>

                {/* Completion status */}
                <div className="mb-3">
                  <p className="text-sm text-gray-700">{buildCompletionStatus(group)}</p>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3">
                  <div className="flex items-center gap-2 text-sm">
                    <ShoppingCart className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600 truncate">
                      <span className="font-medium text-gray-900">
                        {group.numOfOrders}
                      </span>{' '}
                      {group.numOfOrders === 1 ? t('warehouse.modules.packing.order') : t('warehouse.modules.packing.orders')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <ClipboardList className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600 truncate">
                      <span className="font-medium text-gray-900">
                        {group.numOfPickLists}
                      </span>{' '}
                      {group.numOfPickLists === 1 ? t('warehouse.modules.packing.pick') : t('warehouse.modules.packing.picks')}
                    </span>
                  </div>
                </div>

                {/* Additional info */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                  {(() => {
                    // Pakkesoner satt på ordrene ved «Til pakking»; fall tilbake til gruppens PackZoneID
                    const zoneIds = splitPackZoneIds(group.packZoneIDs);
                    if (zoneIds.length === 0 && group.packZoneID) zoneIds.push(group.packZoneID);
                    if (zoneIds.length === 0) return null;
                    return (
                      <span className="truncate">
                        {t('warehouse.modules.packing.zone')}:{' '}
                        <span className="font-medium">{zoneIds.map(getPackZoneLabel).join(', ')}</span>
                      </span>
                    );
                  })()}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
